/**
 * Privacy & Security: what is open, what is stored, and what is never derived.
 *
 * Three things, and they belong together because they answer one question -
 * *what does this product know about me, and how do I take it back?*
 *
 * - **Open sessions**, with the only destructive controls on this screen. The
 *   session you are using has no End button: a control that signs you out of
 *   the page you are on is a trap, and there is already a Sign out for that.
 * - **What this browser keeps.** Three things, named: the email address, the
 *   theme and the viewer's point size. No password, no token - the session is
 *   a cookie JavaScript cannot read - and the remembered address can be
 *   deleted here.
 * - **The face-analysis boundary.** Stated rather than configurable, because
 *   it is not a preference: identity, face signatures, comparison, demographic
 *   and emotion inference are not implemented and are not going to be. A
 *   toggle would imply the other position exists.
 */

import { useState } from "react";
import { Section } from "@/components/product";
import { Button } from "@/components/ui";
import { applyTheme, readTheme, writeTheme } from "@/app/appearance";
import { forgetEmail, readRememberedEmail } from "@/features/auth/rememberedEmail";
import { readPointSize, writePointSize } from "@/features/reconstruction/viewerPreferences";
import { IconEye, IconLock, IconPerson, IconPhone, IconTarget } from "@/components/icons";
import { SessionsSetting } from "../AccountSettings";

const GUARANTEES: { Icon: typeof IconPerson; title: string; body: string }[] = [
  {
    Icon: IconPerson,
    title: "Yalnızca kendi projeleriniz",
    body: "Her istek hesabınızla sınırlanır. Başka bir hesabın projesi, çekimi veya dosyası kimlik numarası bilinse bile açılamaz.",
  },
  {
    Icon: IconLock,
    title: "Geçici, tek kullanımlık kod",
    body: "Eşleştirme kodları kısa süre geçerlidir ve yalnızca bir bağlantı için kullanılır.",
  },
  {
    Icon: IconTarget,
    title: "Yerel ağda bağlantı",
    body: "Telefon bağlantısı yerel ağ üzerinden kurulur. İnternet üzerinden eşleştirme yapılmaz.",
  },
  {
    Icon: IconPhone,
    title: "Kalıcı sır saklanmaz",
    body: "Telefonda kalıcı bir anahtar saklanmaz; bağlantı bilgisi oturum sonunda silinir.",
  },
  {
    Icon: IconEye,
    title: "Yüz tanıma yapılmaz",
    body: "Analiz yalnızca çekimin geometrik yeterliliğini ölçer. Kimlik, yüz imzası, karşılaştırma veya demografik çıkarım üretilmez.",
  },
];

export function PrivacyPanel() {
  // Read once per mount rather than watched: nothing else on this screen can
  // change it, and the only thing that does is the button below.
  const [remembered, setRemembered] = useState(() => readRememberedEmail());
  const [atDefaults, setAtDefaults] = useState(
    () => readTheme() === "system" && readPointSize() === "medium",
  );

  /**
   * Put the two display preferences back, and touch nothing else.
   *
   * The boundary is the point of this control, which is why it sits beside
   * the remembered address rather than away from it: reset restores the theme
   * and the point size, and deliberately does *not* clear that address, end a
   * session, or alter anything about the account. Those are identity and
   * security, they each have their own control on this screen, and a single
   * button that quietly did all of it would be the kind of reset people are
   * right to be afraid of.
   */
  function resetPreferences() {
    writeTheme("system");
    // Written and applied: the stored value is what a reload reads, and the
    // applied one is what the person sees without waiting for one.
    applyTheme("system");
    writePointSize("medium");
    setAtDefaults(true);
  }

  return (
    <>
      <SessionsSetting />

      <Section title="Bu tarayıcıda saklananlar">
        <div className="settings__row">
          <span className="settings__row-label">{"Beni hatırla"}</span>
          <span className="settings__row-value">
            {remembered
              ? `Bu tarayıcıda kayıtlı: ${remembered}`
              : "Bu tarayıcıda kayıtlı e-posta yok."}
          </span>
          {remembered && (
            <Button
              onClick={() => {
                forgetEmail();
                setRemembered(null);
              }}
            >
              {"Kayıtlı e-postayı sil"}
            </Button>
          )}
        </div>
        <p className="muted small settings__hint">Yalnızca e-posta adresi, tema tercihi ve 3D görüntüleyicinin nokta boyutu
          saklanır. Parola veya oturum anahtarı tarayıcıda tutulmaz; oturum,
          JavaScript'in okuyamadığı bir çerezde taşınır.</p>

        <div className="settings__row">
          <span className="settings__row-label">{"Tercihleri sıfırla"}</span>
          <span className="settings__row-value">
            {atDefaults
              ? "Tema ve nokta boyutu zaten varsayılan değerinde."
              : "Temayı Sistem'e, nokta boyutunu Orta'ya döndürür."}
          </span>
          <Button onClick={resetPreferences} disabled={atDefaults}>
            {"Sıfırla"}
          </Button>
        </div>
        {/* Said out loud, because a reset button that does not say where it
            stops is one nobody can safely press. */}
        <p className="muted small settings__hint">
          {"Hesabınıza dokunulmaz: kayıtlı e-posta, açık oturumlar ve parolanız değişmez."}
        </p>
      </Section>

      {/*
        Five guarantees, stated and not switchable.
        
        They are not preferences: each one is a property of how the system was
        built, and a toggle beside any of them would imply the other position
        exists. They live here rather than in a side panel because this is the
        category somebody opens to find out what is known about them.
      */}
      <Section title="Gizlilik ve Güvenlik">
        {GUARANTEES.map(({ Icon, title, body }) => (
          <div className="settings__fact" key={title}>
            <Icon size={18} />
            <div>
              <p className="settings__fact-title">{title}</p>
              <p className="muted small">{body}</p>
            </div>
          </div>
        ))}
      </Section>
    </>
  );
}
